import { useState, useEffect, useCallback, useRef } from 'react';

// TODO v2: Server-side persistence for cross-device timer sync
// TODO v2: Multiple concurrent timers

/** Auto-pause the timer after this many seconds to prevent runaway timers */
const AUTO_PAUSE_THRESHOLD_SECONDS = 24 * 60 * 60; // 24 hours

export interface TimerState {
  matterId: string;
  clientId: string;
  matterName: string;
  clientName: string;
  /** Timestamp when the current running segment started */
  startedAt: number;
  /** Seconds accumulated from previous segments (before pauses) */
  accumulatedSeconds: number;
  /** Whether the timer is currently paused */
  paused: boolean;
}

export interface TimerResult {
  matterId: string;
  clientId: string;
  elapsedSeconds: number;
}

const STORAGE_VERSION = 'v1';

function storageKey(accountId: string): string {
  return `bill_timer_${STORAGE_VERSION}_${accountId}`;
}

function computeElapsed(state: TimerState): number {
  if (state.paused) return state.accumulatedSeconds;
  return state.accumulatedSeconds + Math.floor((Date.now() - state.startedAt) / 1000);
}

function readTimerFromStorage(accountId: string): TimerState | null {
  try {
    const raw = localStorage.getItem(storageKey(accountId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimerState;
    // Validate shape
    if (
      typeof parsed.matterId !== 'string' ||
      typeof parsed.clientId !== 'string' ||
      typeof parsed.startedAt !== 'number'
    ) {
      return null;
    }
    // Backfill for timers created before pause support
    if (typeof parsed.accumulatedSeconds !== 'number') parsed.accumulatedSeconds = 0;
    if (typeof parsed.paused !== 'boolean') parsed.paused = false;
    return parsed;
  } catch {
    return null;
  }
}

function writeTimerToStorage(accountId: string, state: TimerState): void {
  try {
    localStorage.setItem(storageKey(accountId), JSON.stringify(state));
  } catch (err) {
    console.error('Failed to persist timer to localStorage - running in-memory only', err);
  }
}

function clearTimerFromStorage(accountId: string): void {
  try {
    localStorage.removeItem(storageKey(accountId));
  } catch (err) {
    console.error('Failed to clear timer from localStorage', err);
  }
}

export function useTimerState(accountId: string | null) {
  const [autoPaused, setAutoPaused] = useState(false);
  const [timerState, setTimerState] = useState<TimerState | null>(() => {
    if (!accountId) return null;
    const stored = readTimerFromStorage(accountId);
    if (!stored) return null;
    // Auto-pause on load if the timer has been running past the threshold
    if (!stored.paused && computeElapsed(stored) >= AUTO_PAUSE_THRESHOLD_SECONDS) {
      const accumulated = stored.accumulatedSeconds + Math.floor((Date.now() - stored.startedAt) / 1000);
      const updated: TimerState = { ...stored, accumulatedSeconds: accumulated, paused: true };
      writeTimerToStorage(accountId, updated);
      setAutoPaused(true);
      return updated;
    }
    return stored;
  });
  const [elapsed, setElapsed] = useState<number>(() => {
    if (!accountId) return 0;
    const stored = readTimerFromStorage(accountId);
    return stored ? computeElapsed(stored) : 0;
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRunning = timerState !== null && !timerState.paused;
  const isPaused = timerState !== null && timerState.paused;
  const hasTimer = timerState !== null;

  // Drive elapsed updates every second while timer is running (not paused).
  useEffect(() => {
    if (!timerState || timerState.paused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const state = timerState;
    const acctId = accountId;
    intervalRef.current = setInterval(() => {
      const current = computeElapsed(state);
      setElapsed(current);
      // Auto-pause if running past the threshold
      if (acctId && current >= AUTO_PAUSE_THRESHOLD_SECONDS) {
        const accumulated = state.accumulatedSeconds + Math.floor((Date.now() - state.startedAt) / 1000);
        const updated: TimerState = { ...state, accumulatedSeconds: accumulated, paused: true };
        writeTimerToStorage(acctId, updated);
        setTimerState(updated);
        setAutoPaused(true);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, accountId]);

  // Sync across tabs via storage event
  useEffect(() => {
    if (!accountId) return;

    const handleStorage = (e: StorageEvent) => {
      if (e.key !== storageKey(accountId)) return;
      if (e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as TimerState;
          if (typeof parsed.accumulatedSeconds !== 'number') parsed.accumulatedSeconds = 0;
          if (typeof parsed.paused !== 'boolean') parsed.paused = false;
          setTimerState(parsed);
          setElapsed(computeElapsed(parsed));
        } catch {
          // Ignore corrupt data
        }
      } else {
        setTimerState(null);
        setElapsed(0);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [accountId]);

  const start = useCallback(
    (matterId: string, clientId: string, matterName: string, clientName: string) => {
      if (!accountId) return;
      const state: TimerState = {
        matterId,
        clientId,
        matterName,
        clientName,
        startedAt: Date.now(),
        accumulatedSeconds: 0,
        paused: false,
      };
      writeTimerToStorage(accountId, state);
      setTimerState(state);
      setElapsed(0);
      setAutoPaused(false);
    },
    [accountId],
  );

  const pause = useCallback(() => {
    if (!accountId || !timerState || timerState.paused) return;
    const now = Date.now();
    const accumulated = timerState.accumulatedSeconds + Math.floor((now - timerState.startedAt) / 1000);
    const updated: TimerState = { ...timerState, accumulatedSeconds: accumulated, paused: true };
    writeTimerToStorage(accountId, updated);
    setTimerState(updated);
    setElapsed(accumulated);
  }, [accountId, timerState]);

  const resume = useCallback(() => {
    if (!accountId || !timerState || !timerState.paused) return;
    const updated: TimerState = { ...timerState, startedAt: Date.now(), paused: false };
    writeTimerToStorage(accountId, updated);
    setTimerState(updated);
    setAutoPaused(false);
  }, [accountId, timerState]);

  const stop = useCallback((): TimerResult | null => {
    if (!accountId || !timerState) return null;
    const elapsedSeconds = computeElapsed(timerState);
    const result: TimerResult = {
      matterId: timerState.matterId,
      clientId: timerState.clientId,
      elapsedSeconds,
    };
    clearTimerFromStorage(accountId);
    setTimerState(null);
    setElapsed(0);
    return result;
  }, [accountId, timerState]);

  const discard = useCallback(() => {
    if (!accountId) return;
    clearTimerFromStorage(accountId);
    setTimerState(null);
    setElapsed(0);
  }, [accountId]);

  return {
    isRunning,
    isPaused,
    hasTimer,
    autoPaused,
    elapsed,
    timerState,
    start,
    pause,
    resume,
    stop,
    discard,
  };
}
